import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateDDMMYYYY } from '../utils/api';
import { shouldSuppressScan } from '../utils/kioskScan';

const KIOSK_API = process.env.REACT_APP_BACKEND_URL || '';

// How long the success screen stays up + how long new scans are blocked
// after a successful clock-in/out. 15 seconds gives an employee time to
// move away from the camera before it reads their badge again (which
// would otherwise immediately clock them out).
const SUCCESS_COOLDOWN_MS = 15000;
// Errors clear faster so a user can correct a bad scan without waiting.
const ERROR_COOLDOWN_MS = 5000;
// Same-QR suppression lives in utils/kioskScan.js (SAME_QR_SUPPRESS_MS +
// shouldSuppressScan), so the rule can be tested on its own.
// Delay before the missed-finish-time success screen resets back to ready.
const RESET_DELAY = 3000;
// Weekday words for the kiosk header. The date itself is DD/MM/YYYY through
// the house helper; only the weekday word comes from here.
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ClockInKiosk = () => {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scannerRunningRef = useRef(false);
  const resetTimerRef = useRef(null);
  const usbBufferRef = useRef('');
  const usbLastKeyAtRef = useRef(0);
  const handleQRScanRef = useRef(() => {});
  // Same-QR suppression state (refs — don't trigger re-renders). Only a scan
  // that actually worked is remembered; see forgetLastScan below.
  const lastScanTextRef = useRef('');
  const lastScanAtRef = useRef(0);
  const lastScanSucceededRef = useRef(false);

  const [status, setStatus] = useState('ready'); // ready, processing, success, error, missed_prompt
  const [message, setMessage] = useState('');
  const [clockType, setClockType] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [clockTime, setClockTime] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [cameraError, setCameraError] = useState(null);
  const [missedData, setMissedData] = useState(null);
  const [missedFinishTime, setMissedFinishTime] = useState('');
  const [submittingMissed, setSubmittingMissed] = useState(false);
  // Seconds remaining in the post-success cooldown. 0 = cooldown inactive.
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Live clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Initialize camera scanner — optional. The kiosk is fully usable with a
  // USB QR scanner alone; if no camera is present we fail gracefully and
  // surface a message instead of crashing the page.
  useEffect(() => {
    let mounted = true;

    const initScanner = async () => {
      let scanner = null;
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!mounted) return;

        // List cameras BEFORE creating the scanner instance, so we don't
        // hold a half-initialised reference if there's nothing to scan with.
        let cameras = [];
        try {
          cameras = await Html5Qrcode.getCameras();
        } catch (camListErr) {
          console.warn('No camera available:', camListErr?.message || camListErr);
          if (mounted) setCameraError('No camera detected — use a USB scanner instead.');
          return;
        }

        if (!mounted) return;
        if (!cameras || cameras.length === 0) {
          setCameraError('No camera detected — use a USB scanner instead.');
          return;
        }

        scanner = new Html5Qrcode('qr-reader');
        html5QrCodeRef.current = scanner;

        // Prefer back camera on mobile/tablet, first available otherwise
        const backCamera = cameras.find(c => c.label.toLowerCase().includes('back'));
        const cameraId = backCamera ? backCamera.id : cameras[0].id;

        await scanner.start(
          cameraId,
          { fps: 10, qrbox: { width: 300, height: 300 }, aspectRatio: 1.0 },
          (decodedText) => handleQRScanRef.current(decodedText),
          () => {} // ignore errors (no QR in frame)
        );
        if (!mounted) {
          // Component unmounted while we were starting — stop immediately
          try { await scanner.stop(); } catch (_) {}
          return;
        }
        scannerRunningRef.current = true;
      } catch (err) {
        console.warn('Camera init error:', err?.message || err);
        if (mounted) setCameraError('Camera unavailable — use a USB scanner instead.');
        // Drop the half-initialised reference so cleanup doesn't try to stop it
        if (scanner && !scannerRunningRef.current) {
          html5QrCodeRef.current = null;
        }
      }
    };

    initScanner();

    return () => {
      mounted = false;
      // Only attempt to stop if the scanner actually started — html5-qrcode
      // throws synchronously on stop() if it was never started.
      if (html5QrCodeRef.current && scannerRunningRef.current) {
        try {
          const stopResult = html5QrCodeRef.current.stop();
          if (stopResult && typeof stopResult.catch === 'function') {
            stopResult.catch(() => {});
          }
        } catch (_) {
          /* swallow — scanner already torn down */
        }
        scannerRunningRef.current = false;
      }
      html5QrCodeRef.current = null;
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  // USB QR scanner support — most USB QR/barcode scanners present as HID
  // keyboards. They "type" the decoded text very fast and end with Enter.
  // We listen at the document level for keystrokes and treat any sequence
  // ending in Enter (with sub-100ms inter-key gaps) as a scan.
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept keystrokes when the user is editing the missed-clock
      // form input — that would steal characters from a legitimate input.
      const target = e.target;
      const isEditableTarget =
        target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isEditableTarget) return;

      const now = Date.now();
      // If too long since last keystroke, treat as a fresh scan starting
      if (now - usbLastKeyAtRef.current > 200) {
        usbBufferRef.current = '';
      }
      usbLastKeyAtRef.current = now;

      if (e.key === 'Enter') {
        const buffered = usbBufferRef.current;
        usbBufferRef.current = '';
        // Only treat as a scan if we collected something resembling a code
        // (USB scanners deliver many characters in tens of milliseconds).
        if (buffered && buffered.length >= 5) {
          handleQRScanRef.current(buffered);
          e.preventDefault();
        }
        return;
      }

      // Only buffer printable single characters
      if (e.key && e.key.length === 1) {
        usbBufferRef.current += e.key;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Remember a badge that the server accepted, so the camera's next read of
  // the same badge is ignored for SAME_QR_SUPPRESS_MS.
  const rememberLastScan = (text, at) => {
    lastScanTextRef.current = text;
    lastScanAtRef.current = at;
    lastScanSucceededRef.current = true;
  };

  // Forget it after a refusal, so the same badge may be tried again at once
  // and the error message shows again instead of silence.
  const forgetLastScan = () => {
    lastScanTextRef.current = '';
    lastScanAtRef.current = 0;
    lastScanSucceededRef.current = false;
  };

  const handleQRScan = async (decodedText) => {
    // Block anything that isn't the idle state. This covers the full
    // cooldown window (processing + success + error display) AND the
    // missed-clock-out form, which uses its own manual input.
    if (status !== 'ready') return;

    // Suppress rapid re-reads of the exact same badge — the camera
    // decoder fires at 10fps and would otherwise immediately re-trigger
    // a second scan (interpreted by the backend as clock-OUT) the moment
    // the cooldown ends. Only a scan that SUCCEEDED is suppressed: after a
    // refusal the same badge must work straight away and show the message.
    const now = Date.now();
    if (shouldSuppressScan(
      { text: lastScanTextRef.current, at: lastScanAtRef.current },
      now,
      decodedText,
      lastScanSucceededRef.current
    )) {
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(decodedText);
    } catch {
      showError('Invalid QR code format');
      return;
    }

    if (parsed.type !== 'misty_clock' || !parsed.employee_id) {
      showError('Not a valid Misty Manufacturing clock badge');
      return;
    }

    // The last-scan markers are committed further down, once the server has
    // answered. Committing them here suppressed the badge for 30 seconds after
    // a refusal (a 403 off the office network, a 429, a dropped connection),
    // so the next wave of the same badge did nothing and said nothing.
    setStatus('processing');
    setMessage('Processing...');

    let reachedSuccess = false;
    try {
      const resp = await fetch(`${KIOSK_API}/api/kiosk/clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: parsed.employee_id })
      });
      const resData = await resp.json();

      if (!resp.ok) {
        forgetLastScan();
        showError(resData.detail || 'Clock failed');
        return;
      }

      // The server answered and accepted the badge, so this scan counts:
      // remember it and ignore re-reads of the same badge for a while.
      rememberLastScan(decodedText, now);

      // Missed clock-out path — user must type finish_time, keep the
      // form up until they submit or cancel. No auto-reset.
      if (resData.message === 'missed_clock_out' && resData.data) {
        setStatus('missed_prompt');
        setMissedData(resData.data);
        setEmployeeName(resData.data.employee_name || '');
        setMissedFinishTime('');
        return;
      }

      const data = resData?.data || resData;
      setStatus('success');
      setClockType(data.clock_type);
      setEmployeeName(data.employee_name);
      setClockTime(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
      setMessage(data.clock_type === 'in' ? 'Clocked In' : 'Clocked Out');
      reachedSuccess = true;
    } catch (err) {
      forgetLastScan();
      showError(err.message || 'Clock failed');
      return;
    }

    // 15-second cooldown on success: success screen stays, scanner
    // refuses input, countdown ticks visibly.
    if (reachedSuccess) {
      setCooldownRemaining(Math.round(SUCCESS_COOLDOWN_MS / 1000));
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setStatus('ready');
        setMessage('');
        setClockType('');
        setEmployeeName('');
        setClockTime('');
        setCooldownRemaining(0);
      }, SUCCESS_COOLDOWN_MS);
    }
  };

  const showError = (msg) => {
    setStatus('error');
    setMessage(msg);
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setStatus('ready');
      setMessage('');
    }, ERROR_COOLDOWN_MS);
  };

  // Countdown ticker — decrements cooldownRemaining once per second
  // while the cooldown is active. Purely cosmetic; the actual scan
  // block is the `status !== 'ready'` gate in handleQRScan.
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const t = setTimeout(() => setCooldownRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownRemaining]);

  // Keep the ref pointed at the latest closure so the camera + USB listeners
  // (registered once on mount) always invoke the current handleQRScan.
  useEffect(() => {
    handleQRScanRef.current = handleQRScan;
  });

  const handleResolveMissed = async () => {
    if (!missedData || !missedFinishTime) return;
    try {
      setSubmittingMissed(true);
      const resp = await fetch(`${KIOSK_API}/api/kiosk/clock/resolve-missed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ missed_record_id: missedData.missed_record_id, finish_time: missedFinishTime })
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to submit');
      }
      setStatus('success');
      setMessage('Finish time submitted for approval');
      setClockType('');
      setMissedData(null);
      resetTimerRef.current = setTimeout(() => {
        setStatus('ready');
        setMessage('');
        setEmployeeName('');
      }, RESET_DELAY);
    } catch (err) {
      showError(err.message || 'Failed to submit finish time');
    } finally { setSubmittingMissed(false); }
  };

  const formatTime = (d) => d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  // Weekday word in front of the house DD/MM/YYYY date, e.g. "Sunday 13/09/2026".
  const formatDateDisplay = (d) => `${WEEKDAY_NAMES[d.getDay()]} ${formatDateDDMMYYYY(d)}`;

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-yellow-400 rounded-lg flex items-center justify-center">
            <span className="text-black font-bold text-sm">M</span>
          </div>
          <h1 className="text-xl font-bold text-white">Misty Manufacturing — Clock In/Out</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="text-gray-400 hover:text-white text-sm px-4 py-2 border border-gray-600 rounded-lg"
        >
          Back to Dashboard
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-8">
        {/* Live Clock */}
        <div className="text-center mb-8">
          <p className="text-6xl font-mono font-bold text-white tracking-wider">{formatTime(currentTime)}</p>
          <p className="text-lg text-gray-400 mt-2">{formatDateDisplay(currentTime)}</p>
        </div>

        {/* Status Display */}
        {status === 'ready' && (
          <div className="text-center mb-8">
            <p className="text-2xl text-gray-300">Scan your QR badge to clock in or out</p>
          </div>
        )}

        {status === 'processing' && (
          <div className="text-center mb-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
            <p className="text-2xl text-yellow-400">Processing...</p>
          </div>
        )}

        {status === 'success' && (
          <div className={`text-center mb-8 p-8 rounded-2xl border-2 ${
            clockType === 'in' ? 'bg-green-900/30 border-green-500' : 'bg-blue-900/30 border-blue-500'
          }`}>
            <div className={`text-6xl mb-4 ${clockType === 'in' ? 'text-green-400' : 'text-blue-400'}`}>
              {clockType === 'in' ? '\u2713' : '\u2717'}
            </div>
            <p className={`text-4xl font-bold mb-2 ${clockType === 'in' ? 'text-green-400' : 'text-blue-400'}`}>
              {message}
            </p>
            <p className="text-3xl text-white font-semibold">{employeeName}</p>
            <p className="text-xl text-gray-400 mt-2">{clockTime}</p>
            {cooldownRemaining > 0 && (
              <p className="mt-4 text-lg text-gray-500">
                Ready for next scan in {cooldownRemaining}s
              </p>
            )}
          </div>
        )}

        {status === 'error' && (
          <div className="text-center mb-8 p-8 rounded-2xl bg-red-900/30 border-2 border-red-500">
            <p className="text-4xl font-bold text-red-400 mb-2">Error</p>
            <p className="text-xl text-red-200">{message}</p>
          </div>
        )}

        {status === 'missed_prompt' && missedData && (
          <div className="text-center mb-8 p-8 rounded-2xl bg-orange-900/30 border-2 border-orange-500 max-w-lg mx-auto">
            <p className="text-3xl font-bold text-orange-400 mb-2">Missed Clock-Out</p>
            <p className="text-lg text-white mb-1">{employeeName}</p>
            <p className="text-gray-300 mb-4">{missedData.prompt}</p>
            <div className="bg-gray-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-400 mb-1">Clocked in at</p>
              <p className="text-xl text-green-400 font-bold">{missedData.missed_clock_in_time} on {formatDateDDMMYYYY(missedData.missed_date)}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-2">Enter your finish time:</label>
              <input
                type="time"
                value={missedFinishTime}
                onChange={(e) => setMissedFinishTime(e.target.value)}
                className="misty-input text-center text-lg w-40 mx-auto"
                style={{ fontSize: '1.25rem', padding: '0.75rem' }}
              />
            </div>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => { setStatus('ready'); setMissedData(null); }}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleResolveMissed}
                disabled={!missedFinishTime || submittingMissed}
                className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg disabled:opacity-50"
              >
                {submittingMissed ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">This will be sent to managers for review.</p>
          </div>
        )}

        {/* QR Scanner */}
        <div className="relative">
          {cameraError ? (
            <div className="w-80 h-80 bg-gray-800 rounded-2xl flex items-center justify-center border-2 border-gray-600">
              <div className="text-center p-6">
                <p className="text-yellow-400 text-lg mb-2">USB Scanner Mode</p>
                <p className="text-gray-400 text-sm mb-4">{cameraError}</p>
                <p className="text-gray-300 text-sm">Use your connected USB QR scanner to scan a badge.</p>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl overflow-hidden border-4 border-gray-600" style={{ width: 360, height: 360 }}>
              <div id="qr-reader" ref={scannerRef} style={{ width: '100%', height: '100%' }}></div>
            </div>
          )}
          <p className="text-center text-gray-500 text-sm mt-4">
            {cameraError ? 'Scan your QR badge with the USB scanner' : 'Position QR badge in front of the camera or use a USB scanner'}
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-700 px-6 py-3 text-center">
        <p className="text-gray-500 text-sm">Misty Manufacturing Clock-In System — Printing & Graphic Arts Award (MA000026)</p>
      </footer>
    </div>
  );
};

export default ClockInKiosk;
